import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { X } from "@phosphor-icons/react";
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";
import { Goal } from "api/types/aba";

interface SendQabfAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSendAssessment: (behaviorReductionGoal: string) => void;
  clientId: string;
}

/**
 * Dialog for sending a QABF assessment with behavior reduction goal selection.
 * Displays a dropdown to select which behavior reduction goal to assess,
 * with a link to navigate to the Goals page.
 */
export default function SendQabfAssessmentDialog({
  open,
  onClose,
  onSendAssessment,
  clientId,
}: SendQabfAssessmentDialogProps) {
  const [selectedGoalId, setSelectedGoalId] = useState<string>("");
  const { goals, isGoalsLoading } = useGetListOfGoals(clientId, {
    includeArchived: false,
  });
  const clientBrGoals = goals.filter((goal: Goal) => goal.goal_type === "behavior-reduction");

  const handleSendAssessment = () => {
    if (selectedGoalId) {
      onSendAssessment(selectedGoalId);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedGoalId("");
    onClose();
  };

  const handleGoToGoals = () => {
    // Open Client Profile Goals & Targets page in a new tab
    window.open(`/clients/${clientId}/goals-targets`, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="send-qabf-assessment-title"
    >
      <DialogTitle
        id="send-qabf-assessment-title"
        component="div"
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}
      >
        Send QABF Assessment
        <IconButton onClick={handleClose} size="small" aria-label="Close dialog">
          <X weight="regular" size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isGoalsLoading ? (
          <Typography variant="body1">Loading behavior reduction goals...</Typography>
        ) : (
          <Stack spacing={7}>
            <Alert severity="info">
              <AlertTitle>Select a behavior reduction goal</AlertTitle>
              Choose which behavior you'd like to assess using the QABF (Questions About Behavioral
              Function) tool.
            </Alert>

            <FormControl fullWidth>
              <InputLabel id="behavior-goal-label">Behavior reduction goal</InputLabel>
              <Select
                labelId="behavior-goal-label"
                id="behavior-goal-select"
                value={selectedGoalId}
                label="Behavior reduction goal"
                onChange={(e) => setSelectedGoalId(e.target.value.toString())}
              >
                {clientBrGoals.map((goal) => (
                  <MenuItem key={goal.id} value={goal.id}>
                    {goal.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Don't see the behavior you're looking for?{" "}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleGoToGoals}
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                >
                  Go to Goals
                </Link>
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSendAssessment}
          variant="contained"
          color="primary"
          disabled={!selectedGoalId}
        >
          Send Assessment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
